// @ts-strict-ignore
import omit from 'lodash/omit';

import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {
  Entry,
  FiltersOperator,
  isSegmentEntry,
  QueryBehaviorEntry,
  SelectorTypeAggregation,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {generateDatePickerTrackingProps} from 'common/widgets/date-range-picker-new/util';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {getCurrentMoment} from 'common/util/date';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {ALL_MATH_TYPES, EVENTS, PropertyType, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {PRIMITIVE_MATH_TYPES, SpecialTimeUnit} from 'common/types/reports/bookmark';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {
  AGGREGATE_USER_FREQ_BY_GROUP,
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
  BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
  FUNNELS_TIME_TO_CONVERSION,
  PAGE_DURATION,
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {isProperty, Property} from 'common/query-builder/query-block/properties-menu/types';
import {Behavior, BehavioralSegmentation, Segmentation, WindowLengthUnit, WindowType} from 'common/types/reports/query';
import {isCohortSegment, isSegmentation, SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {PATH_SEPARATOR} from 'common/constants';
import {
  PropertySectionItem,
  SectionItemProperty,
} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {renameEvent} from 'common/report/util/events';
import {findSelectedItems} from 'common/components/items-menu/utils';
import {BehavioralAggregationSections} from 'common/query-builder/query-entry-section/row-types/behavior/constants';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {propertiesUtils} from 'common/report/util/properties';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {formatDataGroupLabel} from 'common/query-builder/query-entry-section/util';
import type {GroupLabelParams} from 'common/query-builder/query-block/count-menu/constants';
import {DefaultMultiAttributionLookbackDays} from 'common/widgets/multi-attribution-lookback/constants';
import {SvgIconType} from 'common/components/svg-icon/types';
import {Translatable} from 'common/i18n';

export const DEFAULT_FREQUENCY_BEHAVIOR: Behavior = {
  aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
  event: null,
  filters: [],
  filtersOperator: FiltersOperator.And,
  dateRange: null,
  behaviorType: `$frequency`,
};

export const DEFAULT_AGGREGATION_BEHAVIOR: Behavior = {
  aggregationOperator: null,
  event: null,
  filters: [],
  filtersOperator: FiltersOperator.And,
  dateRange: null,
  behaviorType: `$aggregation`,
};

export const DEFAULT_REVENUE_CHANGE_BEHAVIOR: Behavior = {
  aggregationOperator: undefined,
  event: undefined,
  filters: [],
  filtersOperator: FiltersOperator.And,
  dateRange: null,
  changeType: `any`,
  behaviorType: `$revenue_change`,
};

export const DEFAULT_REVENUE_CHANGE_TYPE_BEHAVIOR: Behavior = {
  aggregationOperator: undefined,
  event: undefined,
  filters: [],
  filtersOperator: FiltersOperator.And,
  dateRange: null,
  behaviorType: `$revenue_change_type`,
};

export const DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR: Behavior = {
  aggregationOperator: ALL_MATH_TYPES.MULTI_ATTRIBUTION,
  event: {
    label: EVENTS[SPECIAL_EVENTS.ANY_EVENT],
    value: SPECIAL_EVENTS.ANY_EVENT,
  },
  filters: [],
  filtersOperator: FiltersOperator.And,
  property: null,
  dateRange: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: DefaultMultiAttributionLookbackDays,
    },
  },
};

export const DEFAULT_BEHAVIORLESS_SEGMENTATION: Segmentation = Object.freeze({
  /** @todo [pablo] figure out behavioral property naming */
  propertyName: `Event${PATH_SEPARATOR}`,
  resourceType: ResourceType.User,
  propertyType: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
});

export const DEFAULT_AGGREGATE_USER_FREQ_BY_GROUP_BEHAVIOR: Behavior = {
  aggregationOperator: ALL_MATH_TYPES.TOTAL,
  event: undefined,
  filters: [
    {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      propertyName: null,
      filterOperator: NumberOperator.AtLeast,
      behavior: {
        aggregationOperator: ALL_MATH_TYPES.TOTAL,
        event: undefined,
        filters: [],
        filtersOperator: FiltersOperator.And,
      },
      filterValue: 1,
    },
  ],
  filtersOperator: FiltersOperator.And,
  dateRange: null,
  behaviorType: `$behavior_aggregation`,
};

export const DEFAULT_EMPTY_BEHAVIORAL_ENTRY: QueryBehaviorEntry = {
  aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  event: null,
  filters: [],
  filtersOperator: FiltersOperator.And,
  behavioralFiltersOperator: FiltersOperator.And,
  property: null,
  type: SelectorTypeDefinitions.Behavior,
  dateRange: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30,
    },
  },
  entryFilter: {
    customProperty: null,
    customPropertyId: null,
    resourceType: ResourceType.Event,
    propertyObjectKey: null,
    propertyDefaultType: PropertyType.Number,
    propertyType: PropertyType.Number,
    filterOperator: NumberOperator.AtLeast,
    filterValue: 1,
  },
};

function isRevenueChangeBehavior(behavior?: Behavior): boolean {
  return !!behavior && behavior.behaviorType === DEFAULT_REVENUE_CHANGE_BEHAVIOR.behaviorType;
}
function isRevenueChangeTypeBehavior(behavior?: Behavior): boolean {
  return !!behavior && behavior.behaviorType === DEFAULT_REVENUE_CHANGE_TYPE_BEHAVIOR.behaviorType;
}
export function isAggregateUserFreqByGroupBehavior(behavior?: Behavior): boolean {
  return !!behavior && behavior.behaviorType === DEFAULT_AGGREGATE_USER_FREQ_BY_GROUP_BEHAVIOR.behaviorType;
}
export function isMultiAttributionBehavior(behavior?: Behavior): boolean {
  return !!behavior && behavior.aggregationOperator === ALL_MATH_TYPES.MULTI_ATTRIBUTION;
}

export function isAggregationBehavior(behavior?: Behavior): boolean {
  return isEventBasedBehavior(behavior) && behavior?.behaviorType === DEFAULT_AGGREGATION_BEHAVIOR.behaviorType;
}

export function isFrequencyBehavior(behavior?: Behavior): boolean {
  return isEventBasedBehavior(behavior) && behavior?.behaviorType === DEFAULT_FREQUENCY_BEHAVIOR.behaviorType;
}

export function isEventBasedBehavior(behavior?: Behavior): boolean {
  return (
    Boolean(behavior) &&
    behavior?.aggregationOperator !== ALL_MATH_TYPES.MULTI_ATTRIBUTION &&
    behavior?.behaviorType !== DEFAULT_AGGREGATE_USER_FREQ_BY_GROUP_BEHAVIOR.behaviorType
  );
}

export function isMultiAttributionSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  return !isCohortSegment(property) && !Array.isArray(property) && isMultiAttributionBehavior(property?.behavior);
}

export function isMultiAttributionSegmentEntry(entry?: Entry): boolean {
  return isSegmentEntry(entry) && isMultiAttributionSegmentation(entry.event);
}

export function isTTCSegmentation(property?: SegmentValue): boolean {
  return (
    !isCohortSegment(property) &&
    !Array.isArray(property) &&
    property?.propertyName === FUNNELS_TIME_TO_CONVERSION.value
  );
}

export function isPageDurationSegmentation(property?: SegmentValue): boolean {
  return !isCohortSegment(property) && !Array.isArray(property) && property?.propertyName === PAGE_DURATION.value;
}

export function isRevenueBehaviorSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  if (isCohortSegment(property)) {
    return false;
  }
  if (Array.isArray(property)) {
    return false;
  }
  return isRevenueChangeBehavior(property?.behavior) || isRevenueChangeTypeBehavior(property?.behavior);
}

export function isAggregateUserFreqByGroupSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  if (isCohortSegment(property)) {
    return false;
  }
  if (Array.isArray(property)) {
    return false;
  }
  return isAggregateUserFreqByGroupBehavior(property?.behavior);
}

// frequency per user
// aggregate property per user
export function isEventBasedBehavioralSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  return (
    !isCohortSegment(property) &&
    !Array.isArray(property) &&
    isEventBasedBehavior(property?.behavior) &&
    !isRevenueBehaviorSegmentation(property)
  );
}

export function isAggregationSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  return !isCohortSegment(property) && !Array.isArray(property) && isAggregationBehavior(property?.behavior);
}

export function isBehaviorSegmentation(property?: SegmentValue): property is BehavioralSegmentation {
  return (
    isMultiAttributionSegmentation(property) ||
    isEventBasedBehavioralSegmentation(property) ||
    isAggregateUserFreqByGroupSegmentation(property)
  );
}

/**
 * Transforms behavioral frequency filters to event based entries
 */
export function transformBehavioralFilterToBehavioralEntry(
  filter: PropertyFilter,
  behavioralFiltersOperator?: FiltersOperator,
): QueryBehaviorEntry {
  const {behavior} = filter.customProperty;
  const property = behavior.property as Property;

  return {
    ...behavior,
    filters: (behavior.filters || []).map((segmentation) => new PropertyFilter(segmentation)),
    behavioralFiltersOperator: behavioralFiltersOperator ?? FiltersOperator.And,
    filtersOperator: behavior?.filtersOperator || FiltersOperator.And,
    type: ResourceType.Event,
    entryFilter: {
      ...filter,
      filterDateUnit: property?.unit as SpecialTimeUnit,
      // an entry filter defines no property
      customProperty: null,
      customPropertyId: null,
      // entry filters act on ResourceType.Event, while the behavioral prop acts on ResourceType.User
      resourceType: ResourceType.Event,
      propertyName: property?.value,
      // pass the aggregated property's nested object-key path so the value dropdown
      // fetches the selected nested property's values instead of the whole top-level object
      ...(property?.propertyObjectKey?.length
        ? {
            propertyObjectKey: property.propertyObjectKey,
            joinPropertyType: property.joinPropertyType,
          }
        : {}),
    },
  };
}

export function getBehaviorTrackingProps(behavior: Behavior) {
  let customProperty, customPropertyId, unit, type, propertyDefaultType;

  if (isProperty(behavior.property)) {
    ({customProperty, customPropertyId, unit, type, propertyDefaultType} = behavior.property);
  }

  const hasPropertyAggregation = Boolean(customProperty || customPropertyId || behavior.property?.value);
  const aggregator = hasPropertyAggregation ? `property_${behavior.aggregationOperator}` : behavior.aggregationOperator;

  return {
    ...generateDatePickerTrackingProps({
      dateRange: new DateRange(behavior.dateRange),
      reportName: null,
      pickerMode: null,
      projectAsOfDate: getCurrentMoment(),
      trackedUseCase: `behavior`,
      projectTz: undefined,
      timePeriodSettings: undefined,
    }),
    'Filters operator': behavior.filtersOperator,
    '# of filters': behavior.filters?.length ?? 0,
    'has 1st time filter': behavior.filters?.some((segmentation) => {
      return isFirstTimeEventFilter(new PropertyFilter(segmentation));
    }),
    // in a behavioral context, all operators are per-user
    Aggregator: aggregator,
    // captures property aggregations
    Property: behavior.property?.value ?? null,
    'Property unit': unit ?? null,
    'Property data type': type ?? null,
    'Property default type': propertyDefaultType ?? null,

    'Event name': behavior.event?.label ?? behavior.event?.value,
    'is custom event': behavior.event?.custom,
    [commonTrackingEventPropertyName.usedUserCustomProperty]: (customPropertyId || customProperty) ?? false,
  };
}

/**
 * Defines tracking props for a behavior entry (usually a behavioral filter)
 * @param entry
 */
export function getBehaviorEntryTrackingProps(entry: QueryBehaviorEntry) {
  const behaviorProps = getBehaviorTrackingProps(entry as Behavior);
  return {
    ...behaviorProps,
    // captures entry filter tracking
    ...(entry.entryFilter
      ? {
          'Filter operator': entry.entryFilter.filterOperator,
          'Filter value': entry.entryFilter.filterValue,
          'Filter type': entry.entryFilter.propertyType,
        }
      : {}),
  };
}

export function hasDynamicLabel(
  item: PropertySectionItem,
): item is SectionItemProperty & {dynamicLabel: (params: GroupLabelParams) => Translatable} {
  return item !== undefined && item !== null && `dynamicLabel` in item;
}

export function getMenuItemForBehavior(
  behavior: Behavior,
  isFilter = false,
  dataGroupId?: string,
  dataGroups?: DataGroup[],
): PropertySectionItem {
  const menuItemMapping = {
    [BEHAVIOR_AGGREGATION.value]: BEHAVIOR_AGGREGATION,
    [BEHAVIOR_FREQUENCY.value]: BEHAVIOR_FREQUENCY,
    [RECURRING_REVENUE_CHANGE.value]: RECURRING_REVENUE_CHANGE,
    [RECURRING_REVENUE_CHANGE_TYPE.value]: RECURRING_REVENUE_CHANGE_TYPE,
    [AGGREGATE_USER_FREQ_BY_GROUP.value]: AGGREGATE_USER_FREQ_BY_GROUP,
  };
  let menuItem = menuItemMapping[behavior.behaviorType];
  if (isMultiAttributionBehavior(behavior)) {
    menuItem = isFilter ? BEHAVIOR_MULTI_ATTRIBUTION_FILTER : BEHAVIOR_MULTI_ATTRIBUTION;
  }

  // Legacy behavioral filters/breakdowns saved before `behaviorType` was added
  // (or with a value no longer in the mapping) leave `menuItem` undefined, which
  // crashes the whole Filter/Breakdown panel. Fall back to a sensible default so
  // these rows still render. This mirrors getLabelForBehavior, which treats
  // unrecognized event-based behaviors as frequency.
  // https://linear.app/mixpanel/issue/COR-557
  if (!menuItem) {
    menuItem = BEHAVIOR_FREQUENCY;
  }

  const labelAndSubheader = hasDynamicLabel(menuItem)
    ? formatDataGroupLabel({label: menuItem.dynamicLabel, dataGroupId, dataGroups})
    : menuItem.label;

  return {
    ...menuItem,
    label: labelAndSubheader as any,
    tooltipData: {
      ...menuItem.tooltipData,
      header: getSelectedLabelforBehavior(behavior, dataGroupId, dataGroups),
      subHeader: labelAndSubheader,
    },
  };
}

export function getAggregationLabelForBehavior(behavior: Behavior): string {
  const sections = generateAggregationsList(behavior);
  // todo: i18n
  return (findSelectedItems(sections)[0]?.label as string) ?? `Select Aggregation...`;
}

export function getIconForBehavior(behavior: Behavior, isFilter = false) {
  return getMenuItemForBehavior(behavior, isFilter).icon as SvgIconType;
}

export function getSelectedLabelforBehavior(
  behavior: Behavior,
  dataGroupId?: string,
  dataGroups?: DataGroup[],
): Translatable {
  return ({print}) =>
    print(getPrefixForBehavior(behavior)) + print(getLabelForBehavior(behavior, dataGroupId, dataGroups));
}

export function getPrefixForBehavior(behavior: Behavior): Translatable {
  if (isMultiAttributionBehavior(behavior)) {
    return ({t}) => t(`query-builder.items.attributed-by.prefix`);
  }
  return ``;
}
export function getColumnLabelForBehavior(behavior: Behavior): Translatable {
  // TODO dataGroup support
  let label;
  const event = renameEvent(behavior.event?.label.toString());
  if (isAggregationBehavior(behavior)) {
    label = ({print}) =>
      `${print(getAggregationLabelForBehavior(behavior))} of ${behavior.property?.label} on ${event}`;
  } else if (isFrequencyBehavior(behavior)) {
    label = `${event} Frequency`;
  }
  return label;
}

export function getLabelForBehavior(behavior: Behavior, dataGroupId?: string, dataGroups?: DataGroup[]): Translatable {
  const groupTitle: Translatable =
    formatDataGroupLabel({
      label:
        ({groupTitle: title}) =>
        ({print}) =>
          print(title),
      dataGroupId,
      dataGroups,
    }) ?? (({t}) => t(`query-builder.dg-user`));

  if (isMultiAttributionBehavior(behavior)) {
    return (i18n) => propertiesUtils.renameProperty(behavior.property?.value, behavior.property?.resourceType, i18n);
  }

  if (isAggregationBehavior(behavior)) {
    return ({t, print}) =>
      t(`query-builder.count-menu.agg-prop-per-dg`, {
        dataGroupName: print(groupTitle),
      });
  }

  if (isRevenueChangeBehavior(behavior)) {
    return `Recurring Revenue Change`;
  }

  if (isRevenueChangeTypeBehavior(behavior)) {
    return `Recurring Revenue Change Type`;
  }

  if (isAggregateUserFreqByGroupBehavior(behavior)) {
    return ({t, print}) =>
      t(`query-builder.count-menu.number-of-users-who-did`, {
        dataGroupName: print(groupTitle),
      });
  }

  return ({t, print}) =>
    t(`query-builder.count-menu.frequency-per-dg`, {
      dataGroupName: print(groupTitle),
    });
}

export function getDefaultPropertyValueForBehavior(isAggregation?: boolean) {
  // TODO better way to tell the diff b/w the two as this is a bit fragile.
  return isAggregation ? null : undefined;
}

export function isBehavioralFilterNegated(entry: QueryBehaviorEntry) {
  return (
    entry.property === null &&
    entry.aggregationOperator === PRIMITIVE_MATH_TYPES.TOTAL &&
    entry.entryFilter?.filterOperator === NumberOperator.EqualTo &&
    entry.entryFilter?.filterValue === 0
  );
}

export function getStepLabel(stepSections, startStepNumber) {
  const defaultStepLabel = `Select Step...`;

  if (startStepNumber === null) {
    // Directly retrieve 'All Steps' label from the first item
    const firstItem = stepSections[0].items[0];
    if (firstItem && firstItem.label === `All Steps`) {
      return firstItem.label;
    }
    return defaultStepLabel;
  }

  // Offset by 1 to account for 'All Steps' item
  const labelIndex = stepSections[0].items[0].label === `All Steps` ? startStepNumber + 1 : startStepNumber;

  return stepSections[0].items[labelIndex]?.label ?? defaultStepLabel;
}

export function generateStepList(behavior: Behavior | Segmentation, stepLen: number): Section[] {
  let items = [];
  if (stepLen) {
    if (stepLen < 2) stepLen = 2;
    items = [...Array(stepLen - 1).keys()].map((i) => ({
      label: `Step ${i + 1} → ${i + 2}`,
      value: i,
      selected: behavior.startStepNumber === i,
    }));
  }

  // If it exists - should always be the first item
  if (isSegmentation(behavior) && behavior.propertyName === FUNNELS_TIME_TO_CONVERSION.value) {
    items.unshift({
      label: `All Steps`,
      value: null,
      selected: behavior.startStepNumber === null,
    });
  }

  return stepLen
    ? [
        {
          items,
        },
      ]
    : [];
}

export function generateAggregationsList(behavior: Behavior) {
  const searchCriteria = {
    forProperty: isAggregationBehavior(behavior),
    operator: behavior.aggregationOperator,
  };

  const itemsMapper = (item: SectionItem<SelectorTypeAggregation>) => ({
    ...item,
    isSelected:
      (item.value?.forProperty || false) === searchCriteria.forProperty &&
      item.value?.operator === searchCriteria.operator,
  });

  return BehavioralAggregationSections.map((section) => ({
    ...section,
    items: section.items?.map(itemsMapper),
  }));
}

export function assignDefaultTimeReference(behavior: Behavior, windowType: WindowType): Behavior {
  const timerReferenceFields = [`windowType`, `startStepNumber`, `endStepNumber`, `windowLength`, `windowLengthUnit`];
  const selection = omit(behavior, timerReferenceFields) as Behavior;

  if (windowType === WindowType.BETWEEN) {
    selection.windowType = windowType;
    selection.startStepNumber = 0;
    selection.endStepNumber = 1;
  }
  if (windowType === WindowType.AFTER) {
    selection.windowType = windowType;
    selection.windowLength = 7;
    selection.windowLengthUnit = WindowLengthUnit.DAY;
  }

  return selection;
}
